import { useMemo } from "react";
import {
  PortfolioDataContext,
  type PortfolioDataContextValue,
} from "@pulse-ui/widget-sdk";
import { useGetOrgsQuery, useGetOrgMetadataQuery } from "@/services/accountApi";
import { useFetchPricingAssetsQuery } from "@/services/portfolioApi";

// Re-export consumer hooks so cloud-ui code can import from this file
export {
  useDashboardData,
  usePortfolios,
  usePortfolioAccounts,
  usePricingAssets,
} from "@pulse-ui/widget-sdk";

/**
 * Fetches org metadata (portfolios + accounts) and pricing assets ONCE for the dashboard.
 * Widgets consume via `usePortfolios()`, `usePortfolioAccounts()`, `usePricingAssets()`,
 * or `useDashboardData()` — all importable from `@pulse-ui/widget-sdk`.
 *
 * Non-blocking — renders children immediately; widgets handle their own empty/loading states.
 */
export function DashboardDataProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const {
    data: org,
    isLoading: orgsLoading,
    isError: orgsError,
  } = useGetOrgsQuery();
  const orgId = org?.org_id;

  const {
    data: orgMetadata,
    isLoading: metaLoading,
    isError: metaError,
  } = useGetOrgMetadataQuery(orgId!, { skip: !orgId });

  const {
    data: rawPricingAssets,
    isLoading: assetsLoading,
  } = useFetchPricingAssetsQuery();

  const portfolios = useMemo(
    () => orgMetadata?.portfolios ?? [],
    [orgMetadata]
  );

  const pricingAssets = useMemo<string[]>(
    () => (Array.isArray(rawPricingAssets) ? rawPricingAssets : []),
    [rawPricingAssets]
  );

  const value = useMemo<PortfolioDataContextValue>(
    () => ({
      portfolios,
      pricingAssets,
      isLoading: orgsLoading || metaLoading || assetsLoading,
      error: orgsError || metaError,
    }),
    [portfolios, pricingAssets, orgsLoading, metaLoading, assetsLoading, orgsError, metaError]
  );

  return (
    <PortfolioDataContext.Provider value={value}>
      {children}
    </PortfolioDataContext.Provider>
  );
}
